/**
 * UnassignedPledges — pledges that belong to no assembly.
 *
 * They predate assembly-scoped giving: a campaign and a giver, but no church,
 * so they appear in no assembly's pledge book, leaderboard or statements. The
 * owner is taken from the giver's own membership and never guessed; what cannot
 * be proved is listed with the reason and left for a human. Every run is
 * reported before it is written, and can be undone.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Undo2, UserCheck } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useAuthStore } from '@/lib/store';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REASON_LABELS = {
  no_contact_details: 'no email or telephone on the pledge',
  no_membership_found: 'no membership matches those details',
  member_of_more_than_one: 'the giver is a member in more than one assembly',
  contact_details_disagree: 'the telephone and the email point at different people',
};

export const UnassignedPledges = () => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [plan, setPlan] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const { data: runs } = useQuery({
    queryKey: ['pledge-attribution-runs'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/finance/attribution/pledges/assembly-backfill/runs`,
                            { headers });
      if (!r.ok) throw new Error('unavailable');
      return r.json();
    },
    enabled: !!token,
    retry: false,
  });

  const preview = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API_URL}/api/finance/attribution/pledges/assembly-backfill`,
                            { method: 'POST', headers });
      if (!r.ok) throw new Error('Could not check the pledges');
      return r.json();
    },
    onSuccess: (d) => {
      setPlan(d);
      toast.success(`${d.unassigned} without an assembly · ${d.would_assign} can be proved`);
    },
    onError: (e) => toast.error(e.message),
  });

  const assign = useMutation({
    mutationFn: async () => {
      const r = await fetch(
        `${API_URL}/api/finance/attribution/pledges/assembly-backfill?apply=true`,
        { method: 'POST', headers });
      if (!r.ok) throw new Error('Could not assign the pledges');
      return r.json();
    },
    onSuccess: (d) => {
      setPlan(d);
      queryClient.invalidateQueries({ queryKey: ['pledge-attribution-runs'] });
      toast.success(`${d.assigned} pledge${d.assigned === 1 ? '' : 's'} given to their assembly`);
    },
    onError: (e) => toast.error(e.message),
  });

  const undo = useMutation({
    mutationFn: async (runId) => {
      const r = await fetch(
        `${API_URL}/api/finance/attribution/pledges/assembly-backfill/${runId}/undo`,
        { method: 'POST', headers });
      if (!r.ok) throw new Error('Could not undo that run');
      return r.json();
    },
    onSuccess: (d) => {
      queryClient.invalidateQueries({ queryKey: ['pledge-attribution-runs'] });
      toast.success(`${d.reverted || 0} pledge(s) put back`);
    },
    onError: (e) => toast.error(e.message),
  });

  const matches = plan?.matches || [];
  const working = preview.isPending || assign.isPending;

  return (
    <Card data-testid="unassigned-pledges-panel">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-indigo-500" />
            Pledges with no assembly
          </CardTitle>
          <CardDescription>
            Given to the assembly the giver belongs to — never guessed
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={working}
                  onClick={() => preview.mutate()} data-testid="unassigned-preview">
            {preview.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Check first
          </Button>
          <Button size="sm" disabled={working || !plan?.would_assign}
                  onClick={() => assign.mutate()} data-testid="unassigned-apply">
            {assign.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Assign {plan?.would_assign ? `the ${plan.would_assign}` : ''}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!plan ? (
          <p className="text-sm text-slate-500" data-testid="unassigned-idle">
            Check first shows what would change and writes nothing.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              {[['Without an assembly', plan.unassigned, 'unassigned-total'],
                [plan.dry_run ? 'Can be proved' : 'Assigned',
                  plan.would_assign ?? plan.assigned, 'unassigned-provable'],
                ['Left for a human', plan.left_alone, 'unassigned-left']].map(
                ([label, value, testid]) => (
                  <div key={label} className="rounded-lg border bg-slate-50 p-3"
                       data-testid={testid}>
                    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
                    <p className="text-2xl font-semibold text-slate-800">{value ?? 0}</p>
                  </div>
                ))}
            </div>

            {Object.entries(plan.reasons || {}).some(([, n]) => n > 0) ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3"
                   data-testid="unassigned-reasons">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-900">
                  <AlertTriangle className="h-3.5 w-3.5" />Left alone, and why
                </p>
                <ul className="mt-1 space-y-0.5 text-xs text-amber-800">
                  {Object.entries(plan.reasons).filter(([, n]) => n > 0).map(([key, n]) => (
                    <li key={key}>{n} · {REASON_LABELS[key] || key.replace(/_/g, ' ')}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {matches.length ? (
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Giver</TableHead>
                      <TableHead>Goes to</TableHead>
                      <TableHead>Proved by</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {matches.map((m, i) => (
                      <TableRow key={m.pledge_id} data-testid={`unassigned-row-${i}`}>
                        <TableCell>
                          {m.donor_name}
                          <span className="block text-xs text-slate-400">{m.donor_email}</span>
                        </TableCell>
                        <TableCell>{m.assembly || m.church_id}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{m.matched_by}</Badge>
                          <span className="ml-1.5 text-xs text-slate-400">{m.member_name}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          {m.currency} {Number(m.amount || 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : null}
          </>
        )}

        {runs?.runs?.length ? (
          <div className="space-y-1.5" data-testid="unassigned-runs">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Previous runs
            </p>
            {runs.runs.map((r) => (
              <div key={r.run_id}
                   className="flex items-center justify-between rounded border px-3 py-1.5 text-xs">
                <span className="text-slate-600">
                  {String(r.at).slice(0, 16).replace('T', ' ')} · {r.assigned} assigned
                  {r.undone ? ' · undone' : ''}
                </span>
                {r.undone ? null : (
                  <Button variant="ghost" size="sm" className="h-7 text-xs"
                          onClick={() => undo.mutate(r.run_id)}
                          data-testid={`unassigned-undo-${r.run_id}`}>
                    <Undo2 className="mr-1 h-3.5 w-3.5" />Undo
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default UnassignedPledges;
